import type { NextPageParams } from "@/schema"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { useBookId } from "@/hooks/use-book-id"

import { fetchMeetingVersion, fetchMeetingVersions } from "./api"

export function useFetchMeetingVersion(meetingId: string, versionId?: string) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { data: user } = useFetchCurrentUser()
  return useQuery({
    queryKey: ["meeting-version", user?.id, bookId, meetingId, versionId],
    queryFn: async () => {
      if (!versionId) throw new Error("Meeting version id is required")
      return fetchMeetingVersion(await getContext(), meetingId, versionId)
    },
    enabled: Boolean(versionId),
  })
}

export function useInfiniteMeetingVersions(meetingId: string) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { data: user } = useFetchCurrentUser()
  return useInfiniteQuery({
    queryKey: ["meeting-versions", user?.id, bookId, meetingId],
    queryFn: async ({ pageParam }: { pageParam: NextPageParams | undefined }) =>
      fetchMeetingVersions(await getContext(), meetingId, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
  })
}
